import { Controller } from "@hotwired/stimulus"

// Listens for `notification:show` events dispatched on `window` and displays
// a transient toast popup. Callers fire:
//
//   window.dispatchEvent(new CustomEvent("notification:show", {
//     detail: { title: "Oops!", message: "Something went wrong.", type: "error" }
//   }))
//
// Supported types: "error" (default) | "success"
// The panel auto-dismisses after `autoDismissDelay` ms (default 5 000).
export default class extends Controller {
  static targets = ["panel", "title", "message", "iconError", "iconSuccess"]
  static values  = { autoDismissDelay: { type: Number, default: 5000 } }

  #dismissTimer = null
  #boundShow    = null

  connect() {
    this.#boundShow = this.#show.bind(this)
    window.addEventListener("notification:show", this.#boundShow)
  }

  disconnect() {
    window.removeEventListener("notification:show", this.#boundShow)
    clearTimeout(this.#dismissTimer)
  }

  close() {
    this.#animateOut()
  }

  // ── private ──────────────────────────────────────────────────────────────

  #show(event) {
    const { title = "", message = "", type = "error" } = event.detail

    if (this.hasTitleTarget)   this.titleTarget.textContent   = title
    if (this.hasMessageTarget) this.messageTarget.textContent = message

    this.#applyType(type)
    this.#animateIn()

    clearTimeout(this.#dismissTimer)
    this.#dismissTimer = setTimeout(() => this.#animateOut(), this.autoDismissDelayValue)
  }

  // Make visible at the offset position, then transition to resting position on the next frame.
  #animateIn() {
    const panel = this.panelTarget

    panel.classList.remove("hidden")
    panel.classList.add("translate-y-4", "opacity-0")

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.classList.remove("translate-y-4", "opacity-0")
        panel.classList.add("translate-y-0", "opacity-100")
      })
    })
  }

  // Reverse transition back to offset, then hide once the animation ends.
  #animateOut() {
    const panel = this.panelTarget

    panel.classList.remove("translate-y-0", "opacity-100")
    panel.classList.add("translate-y-4", "opacity-0")

    panel.addEventListener("transitionend", () => {
      panel.classList.add("hidden")
      panel.classList.remove("translate-y-4", "opacity-0")
    }, { once: true })
  }

  #applyType(type) {
    const isError = type === "error"
    if (this.hasIconErrorTarget)   this.iconErrorTarget.classList.toggle("hidden", !isError)
    if (this.hasIconSuccessTarget) this.iconSuccessTarget.classList.toggle("hidden", isError)
  }
}
